import { DefaultLayout } from '@c/layouts';
import ProductsPage from '@c/ProductsPage';
import SEO from '@c/SEO';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { ListingsFilters, PaginationFilters } from '@util/hooks/useProductsPage';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import { GetServerSideProps } from 'next';
import { ReactElement } from 'react';
import { getProductsFromAlgolia } from '@util/firestore/products';
import { CategoryDocument } from '@util/firestore/categories';
import { BrandDocument } from '@util/types/firestore/brand';
import { ConditionDocument } from '@util/types/firestore/conditions';

interface SearchProps {
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: PaginationFilters;
  initialSideFilters: ListingsFilters;
  keyword: string;
  categoryOptions: CategoryDocument[];
  brandOptions: BrandDocument[];
  conditionOptions: ConditionDocument[];
}

const Search = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  keyword,
  categoryOptions,
  brandOptions,
  conditionOptions,
}: SearchProps) => {
  return (
    <>
      <SEO
        title={`Search Results for "${keyword}"`}
        description={`Found ${initialPageInfo.totalResults} camera gear items matching "${keyword}". Browse used cameras, lenses, and photography equipment.`}
        noIndex={true}
        keywords={[keyword, 'camera gear', 'photography equipment', 'used cameras']}
      />

      {/*
        GB #1401 gate-review fix (CRITICAL): useProductsPage seeds its
        products/pageInfo state via useState(results)/useState(pageInfo) with
        no effect re-syncing them when new SSR props arrive on a client-side
        navigation. _app.tsx renders <Component {...pageProps} /> with no
        key, and the header SearchBar's goToSearch navigates via
        router.push (no full reload) — so searching again from an existing
        /search results page previously kept the OLD keyword's products and
        pageInfo frozen while only the header/H1 updated to the new keyword.
        Keying on `keyword` forces React to unmount/remount the whole
        ProductsPage subtree (and therefore useProductsPage) whenever the
        keyword changes, so the new SSR-fetched results always render.
        Scoped to search.tsx only — the shared useProductsPage hook itself is
        NOT touched, so category/brand/collections/products/seller pages are
        unaffected.
      */}
      <ProductsPage
        key={keyword}
        initialProducts={initialProducts}
        initialPageInfo={initialPageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        headerBreadcrumb={[
          { label: 'Home', href: '/' },
          { label: 'Search', href: '/search' }
        ]}
        pageHeaderTitle={`${initialPageInfo.totalResults} Results for "${keyword}"`}
        pageType="search"
        keyword={keyword}
        seller_id={null}
        categoryOptions={categoryOptions}
        slugToTitle={undefined}
        brandOptions={brandOptions}
        conditionOptions={conditionOptions}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<SearchProps> = async (
  context
) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );

  const args = await getProductQueryArgsFromContext(context);
  const response = await getProductsFromAlgolia(args);

  return {
    props: {
      initialProducts: response.results,
      initialPageInfo: response.pageInfo,
      categoryOptions: args.categoryOptions,
      conditionOptions: args.conditionOptions,
      brandOptions: args.brandOptions,
      initialTopFilters: {
        sort: args.sort,
        limit: args.limit,
        view: (context.query.view as 'grid') || 'list',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        condition: args.conditionFilters,
        category: args.categoryFilters,
        brand: args.brandFilters,
        deals: args.deals,
        useCases: args.useCases,
        additional: args.additionalFilters
      },
      keyword: args.keyword ?? '',
    },
  };
};

Search.getLayout = (page: ReactElement) => {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default Search;
